import type { MenuTab } from "../hud/panels/shared";
import { CELL_TILES } from "./ground";
import type { MapBounds, SurveyedMap } from "./survey";

/*
 * The map, drawn: a sheet of paper with the surveyed ground on it.
 *
 * The menu's third page, and the only one of the four that is a *picture*
 * rather than a list of strings — which is why it is here rather than under
 * `hud/panels/`. It keeps that collection's two input rules exactly (`.hud` is
 * `pointer-events: none` and anything that wants the mouse turns it back on for
 * itself; every surface closes from the keyboard as well as from its own
 * button), and it keeps the split `journal/journal.ts` states: the state and
 * the rules are in `map/ground.ts` and `map/survey.ts`, and this only draws
 * what they hand over.
 *
 * Since chunk U-3 it is handed a {@link MenuTab} rather than the HUD's root, so
 * it fills a page of the screen instead of standing in a corner of the frame —
 * and it never learns what a menu is, which is why that type is an interface in
 * `hud/panels/shared.ts` rather than the class.
 *
 * # What is not on it
 *
 * No compass, no heading arrow, no player dot, no markers, no beacons — the
 * roadmap's minimal-chrome policy and Phase 5's cut list, verbatim: *wayfinding
 * stays diegetic — the map is the answer, not a minimap*. There is not even a
 * north: nothing in this world has ever been called north, and inventing a
 * bearing is inventing a fact about the world. What orients a reader is their
 * own trail, whose end is where they are standing — which is the same
 * affordance a real sketch map gives and costs the fiction nothing.
 *
 * # The look
 *
 * PROVISIONAL, and it is a taste call on the owner's screenshot pass (the
 * art-pass precedent, `docs/plans/gate-a.md`). Ink rather than colour, because
 * everything on it is a line somebody drew — and the sheet the ink is on is
 * **charcoal** since chunk U-3, which is the inversion the aged sheet's own
 * note invited. Both palettes are written out below and one word says which is
 * live ({@link SHEET}), so the owner asking for the sepia back is one edit.
 */

/** One sheet's worth of colour: the paper, the ground on it and the ink. */
interface Sheet {
  /**
   * The unsurveyed colour, and the one thing about a palette here that is
   * structural rather than taste: blank paper is what the fog of exploration
   * looks like, so it has to be plainly a different thing from the shaded
   * ground beside it. On both sheets below it sits **outside** the ramp — above
   * it on the sepia, below it on the charcoal.
   */
  readonly paper: string;

  /** The towns and their names: the thing a reader is looking for. */
  readonly ink: string;

  /** The shading ramp, low ground to high. */
  readonly low: string;
  readonly high: string;

  /** The roads somebody built, the trail this player walked, and the marks. */
  readonly road: string;
  readonly trail: string;
  readonly mark: string;
}

/**
 * The aged sheet: sepia, dim enough to live in a dark HUD, the trail the
 * quietest ink on it.
 *
 * **Kept, and kept beside the one that is live**, so *"give me the sheet back"*
 * is one word changed on {@link SHEET} rather than seven colours retyped.
 */
const SEPIA: Sheet = {
  paper: "#cabb99",
  ink: "#3a2f21",
  low: "#5f6f45",
  high: "#a8a173",
  road: "rgb(104 68 38 / 85%)",
  trail: "rgb(58 47 33 / 55%)",
  mark: "#5e3f22",
};

/**
 * The charcoal sheet, and it is the inversion the sepia's own note invited
 * (chunk U-3, `ui-lab/surfaces/menu-tabs.html`'s first flagged departure).
 *
 * The argument for it is the panel: inside a dark glass screen an aged sheet is
 * the brightest thing on the display by a distance, and the map page is now
 * three quarters of the frame rather than a box in the middle of it. So the
 * paper goes dark, the ground goes with it, and the ink comes up.
 *
 * **One thing genuinely swaps rather than inverting**, and it is the lab's own
 * call: the *trail* takes the accent. On the sepia sheet the roads are the
 * strong ink and the walked line is a whisper; on a dark sheet the brightest
 * ink should be the thing the player put there themselves, which is the only
 * line on the paper that is theirs — and it is also the whole of this map's
 * wayfinding, because its end is where they are standing.
 *
 * PROVISIONAL, like the sepia it replaces, and for the same reason: it is a
 * taste call on the owner's screenshot pass (the art-pass precedent,
 * `docs/plans/gate-a.md`).
 */
const CHARCOAL: Sheet = {
  paper: "#141516",
  ink: "#e2dccd",
  // Below the paper rather than above it, which is the structural rule read the
  // other way up: surveyed ground has to be plainly a different thing from the
  // blank sheet, and on a dark page the way to be different is to be lighter.
  low: "#2b3327",
  high: "#565448",
  // Quieter than the sepia sheet's roads relative to everything else on the
  // page, which is the swap below working: on a dark sheet the brightest ink
  // has to be the trail, and a solid 2.4px line beats a dashed 1.4px one at
  // equal weight. Judged on the still, not calculated.
  road: "rgb(146 116 76 / 72%)",
  trail: "rgb(199 170 106 / 62%)",
  mark: "rgb(226 220 205 / 55%)",
};

/**
 * The two sheets, by name, and which one the paper is drawn on.
 *
 * **One word, by design.** The charcoal is the live palette and the sepia is
 * kept beside it rather than deleted, because both are PROVISIONAL until the
 * owner has looked at a screenshot of each — and *"give me the sheet back"* has
 * to be a word rather than seven colours retyped from a diff.
 */
const SHEETS = { sepia: SEPIA, charcoal: CHARCOAL } as const;
const SHEET: Sheet = SHEETS.charcoal;

/**
 * How much fainter ground the player only *saw* is drawn than ground they
 * walked.
 *
 * The fringe is a real distinction and it is drawn as one: what you crossed is
 * on the page at full strength, what you looked at from a hillside is a wash.
 */
const SEEN_ALPHA = 0.62;

/** Line widths, in screen pixels, so they stay legible at every zoom. */
const ROAD_WIDTH = 2.4;
const TRAIL_WIDTH = 1.4;

/** The dashes the trail is drawn with: a walked line, not a road. */
const TRAIL_DASH = [3, 4];

/**
 * How wide the paper is, in tiles, at the two ends of the zoom.
 *
 * The close end is two view distances (`world/wasm.ts` puts the fog at eighty),
 * which is as much ground as a player can take in from one standing and
 * therefore the finest the paper has anything to say at. The far end is several
 * days of walking, which is more world than one session covers.
 */
const FINEST_TILES_ACROSS = 160;
const COARSEST_TILES_ACROSS = 6000;

/** What one wheel notch does to the span. A twelfth, so a scroll is a nudge. */
const ZOOM_STEP = 1.12;

/** The span an empty page is drawn at, so the first cell to arrive has a scale. */
const EMPTY_TILES_ACROSS = 400;

/** What the canvas is drawn at when the layout has not given it a size yet. */
const NOMINAL_WIDTH = 640;
const NOMINAL_HEIGHT = 420;

/** How big a settlement reads on the paper, in screen pixels. */
const PLACE_RADIUS = 3.5;
const MARK_SIZE = 3;

/**
 * The label face and size, in canvas pixels.
 *
 * **Serif since chunk U-3**, which is the register rule the whole client took
 * at the same time: serif is what the world says, and every string on this
 * paper is a name the simulation spelled. The stack is read off the kit's own
 * `--serif` rather than typed here, so the day the display face is chosen
 * (plan decision D5) the paper follows without a second edit — a canvas cannot
 * inherit a custom property, so it is asked for once and kept.
 *
 * The size is PROVISIONAL with the sheet, and the line height goes with it:
 * two labels closer than that touch.
 */
const LABEL_SIZE = 12;
const LABEL_FALLBACK = 'Georgia, "Times New Roman", serif';

/** The stack, asked for once. `null` until the first draw. */
let labelFace: string | null = null;

/** The kit's `--serif`, or the stack it is written as. */
function labelFont(): string {
  if (labelFace === null) {
    const named = getComputedStyle(document.documentElement)
      .getPropertyValue("--serif")
      .trim();

    labelFace = named === "" ? LABEL_FALLBACK : named;
  }

  return `${LABEL_SIZE}px ${labelFace}`;
}

/**
 * How far apart two stacked labels sit, in screen pixels, and the offsets one is
 * tried at.
 *
 * The line height is the font's own with a little air — two labels closer than
 * this touch. The steps are its own height first, then one line down, one up,
 * two down, two up: nearest-first, so a name stays as close to the thing it
 * names as the page allows, and five slots because a sixth would be a label
 * halfway across the paper from its mark.
 */
const LABEL_LINE_HEIGHT = 14;
const LABEL_STACK_STEPS = [0, 1, -1, 2, -2];

/**
 * The map: the menu's third page, landed on with `M`, drawing only what the
 * survey allows.
 *
 * Pan by dragging and zoom by wheel, both of which need the mouse — which the
 * page has, because opening the menu hands the pointer lock back (`main.ts`).
 * The keyboard closes it, like every other page of the screen.
 *
 * **It is a page rather than a panel since chunk U-3** and nothing inside the
 * sheet moved with it: what the canvas draws, what the survey allows onto it
 * and the palette below are all exactly what they were. What went is the box —
 * a title bar and a hint of its own, in a corner, beside two other boxes that
 * did not know it existed.
 */
export class MapPanel {
  private readonly tab: MenuTab;
  private readonly titleElement: HTMLElement;
  private readonly countElement: HTMLElement;
  private readonly noteElement: HTMLElement;
  private readonly canvas: HTMLCanvasElement;

  /** The last thing handed over, kept so a pan or a zoom can redraw without a re-survey. */
  private survey: SurveyedMap | null = null;

  /** Where the middle of the paper is, in world tiles, and how much of it is across. */
  private centreX = 0;
  private centreY = 0;
  private tilesAcross = EMPTY_TILES_ACROSS;

  /**
   * Whether the view has been fitted to the survey since the page was arrived
   * at.
   *
   * Fitted once on arrival and never again while it is being read: a day
   * boundary refills this page underneath a reader (`witness/witness.ts`), and
   * a refill that snapped the paper back to a fitted view would take it out of
   * their hands while they were looking at it. What counts as an arrival is
   * {@link MapPanel.show}'s note.
   */
  private fitted = false;

  /** Where the drag started, in screen pixels, or `null` when nobody is dragging. */
  private dragging: { x: number; y: number } | null = null;

  private draws = 0;

  /**
   * `tab` is the menu's map page; `touch` decides whether the page's own note
   * offers a wheel nobody turns.
   */
  constructor(tab: MenuTab, touch = false) {
    this.tab = tab;

    this.canvas = document.createElement("canvas");
    this.canvas.className = "hud__map-paper";

    // What the page is, under the sheet: *"drawn from the ground you have
    // walked"*, or the empty state. Bookkeeping about the paper rather than
    // anything the world said, so sans (`subtitleFor`).
    this.countElement = document.createElement("div");
    this.countElement.className = "hud__panel-count";

    // And when it was drawn, at the far end of the same line. A hand-drawn
    // chart is a thing somebody drew on a day, and the date is the honest
    // answer to *how old is this*.
    this.titleElement = document.createElement("div");
    this.titleElement.className = "hud__panel-title";

    const under = document.createElement("div");
    under.className = "hud__map-under";
    under.append(this.countElement, this.titleElement);

    this.noteElement = document.createElement("div");
    this.noteElement.className = "hud__map-margin";
    this.noteElement.hidden = true;

    tab.body.append(this.canvas, under, this.noteElement);

    // What can be done *to* the paper, and nothing about closing it — the menu
    // says that once for all four pages (`hud/panels/menu.ts`). The wheel goes
    // on a touchscreen: there is no scroll gesture over the paper, so the note
    // says the one thing that is true there and pinching, which is not
    // implemented, is not advertised (`input/touch.ts`, `input.md` §9).
    tab.note(
      touch ? "drag to move the paper" : "drag to move the paper · scroll to zoom",
    );

    this.canvas.addEventListener("pointerdown", this.handlePointerDown);
    this.canvas.addEventListener("pointermove", this.handlePointerMove);
    this.canvas.addEventListener("pointerup", this.handlePointerUp);
    this.canvas.addEventListener("pointercancel", this.handlePointerUp);
    this.canvas.addEventListener("wheel", this.handleWheel, { passive: false });
  }

  get open(): boolean {
    return this.tab.showing;
  }

  /**
   * How many times the paper has been filled in.
   *
   * For the self-checks, and it is the only honest way to assert a *refresh*:
   * whether a panel left open across a day boundary was redrawn is invisible in
   * what it shows, because the day usually changes nothing on it
   * (`witness/witness.ts` joins the same list the market and the journal are
   * on). `Witnessing.watches` is on the screen for the same reason.
   */
  get shown(): number {
    return this.draws;
  }

  /**
   * Fills the paper from a survey and shows it.
   *
   * `title` is the panel's heading and the caller composes it, the way the
   * market panel's is composed by the side of the client that knows which town
   * it is showing (`hud/panels.ts`).
   */
  show(survey: SurveyedMap, title: string): void {
    this.survey = survey;
    this.titleElement.textContent = dateOf(title);

    // **Arriving at the page is what "opening" means now.** The fit runs once
    // per opening and never again while the paper is being read — a day
    // boundary and every couple of seconds of walking refill this page under
    // the reader (`witness/witness.ts`), and a refill that snapped the sheet
    // back would take it out of their hands. Before U-3 the reset lived in
    // `hide`, which was reached by the `M` toggle; the screen can now be closed
    // without this page being told, so the arrival is asked about here instead.
    if (!this.tab.showing) {
      this.fitted = false;
    }

    if (!this.fitted) {
      this.fit(survey.bounds);
      this.fitted = true;
    }

    this.countElement.textContent = subtitleFor(survey);

    this.noteElement.hidden = survey.heard.length === 0;
    // Names and nothing else, because a name is all that was learned: meeting
    // somebody from a place teaches you what it is called, not where it is
    // (`map/survey.ts`).
    this.noteElement.textContent =
      survey.heard.length === 0 ? "" : `places you have only been told of: ${survey.heard.join(" · ")}`;

    this.tab.reveal();
    this.draw();
  }

  hide(): void {
    this.tab.close();
    this.dragging = null;
    // The next opening is a fresh look at the page, and a fresh look starts
    // where the ground is rather than wherever the last pan left off.
    this.fitted = false;
  }

  private readonly handlePointerDown = (event: PointerEvent): void => {
    this.dragging = { x: event.clientX, y: event.clientY };
    // Captured, so a drag that leaves the paper keeps moving it and a button
    // released off the panel still ends the drag. No `dispose` to match it:
    // every listener here is on this panel's own canvas and goes with it, the
    // way the other panels' do (`hud/panels.ts` has none to remove either).
    this.canvas.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    const from = this.dragging;

    if (from === null) {
      return;
    }

    const scale = this.scale();

    // The paper moves with the hand: dragging right pulls the sheet right,
    // which is the centre moving left. Screen y is down and the world's is up.
    this.centreX -= (event.clientX - from.x) / scale;
    this.centreY += (event.clientY - from.y) / scale;
    this.dragging = { x: event.clientX, y: event.clientY };
    this.draw();
  };

  private readonly handlePointerUp = (event: PointerEvent): void => {
    this.dragging = null;

    // Asked first: releasing a capture the browser has already let go of
    // throws, and the implicit release on `pointerup` is exactly that case.
    if (this.canvas.hasPointerCapture(event.pointerId)) {
      this.canvas.releasePointerCapture(event.pointerId);
    }
  };

  private readonly handleWheel = (event: WheelEvent): void => {
    // The page underneath must not scroll; the panel owns the wheel while the
    // pointer is over the paper.
    event.preventDefault();

    const step = event.deltaY > 0 ? ZOOM_STEP : 1 / ZOOM_STEP;

    this.tilesAcross = Math.min(
      COARSEST_TILES_ACROSS,
      Math.max(FINEST_TILES_ACROSS, this.tilesAcross * step),
    );
    this.draw();
  };

  /** Puts the whole survey on the paper, with a little air around it. */
  private fit(bounds: MapBounds | null): void {
    if (bounds === null) {
      this.centreX = 0;
      this.centreY = 0;
      this.tilesAcross = EMPTY_TILES_ACROSS;

      return;
    }

    this.centreX = (bounds.minX + bounds.maxX) / 2;
    this.centreY = (bounds.minY + bounds.maxY) / 2;

    const { width, height } = this.size();
    const span = Math.max(
      bounds.maxX - bounds.minX,
      // What a width of tiles is worth vertically, so a tall survey fits too.
      ((bounds.maxY - bounds.minY) * width) / Math.max(1, height),
    );

    this.tilesAcross = Math.min(
      COARSEST_TILES_ACROSS,
      Math.max(FINEST_TILES_ACROSS, span * 1.15),
    );
  }

  /** The canvas's size in CSS pixels, or a nominal one before it has been laid out. */
  private size(): { readonly width: number; readonly height: number } {
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;

    return width > 0 && height > 0
      ? { width, height }
      : { width: NOMINAL_WIDTH, height: NOMINAL_HEIGHT };
  }

  /** Screen pixels per world tile, at the current zoom. */
  private scale(): number {
    return this.size().width / this.tilesAcross;
  }

  /**
   * Draws the paper.
   *
   * Everything it puts down came out of the survey — no query, no sampler, no
   * second opinion about what is where (`map/survey.ts`). That is what makes
   * "nothing on the map is authored or revealed for free" a thing the
   * self-checks can prove by looking at the survey alone.
   */
  private draw(): void {
    const survey = this.survey;
    const context = this.canvas.getContext("2d");

    if (survey === null || context === null) {
      return;
    }

    const { width, height } = this.size();
    // A backing store at the device's own resolution, so ink on a retina panel
    // is a line rather than a smear. Written every draw because a panel can be
    // opened after the window moved to another display.
    const ratio = Math.min(window.devicePixelRatio || 1, 3);

    this.canvas.width = Math.max(1, Math.round(width * ratio));
    this.canvas.height = Math.max(1, Math.round(height * ratio));
    context.setTransform(ratio, 0, 0, ratio, 0, 0);

    const scale = width / this.tilesAcross;
    const toX = (tileX: number): number => (tileX - this.centreX) * scale + width / 2;
    const toY = (tileY: number): number => height / 2 - (tileY - this.centreY) * scale;

    context.fillStyle = SHEET.paper;
    context.fillRect(0, 0, width, height);

    const band = Math.max(1, survey.highest - survey.lowest);
    const side = Math.ceil(CELL_TILES * scale) + 1;

    for (const cell of survey.cells) {
      context.globalAlpha = cell.walked ? 1 : SEEN_ALPHA;
      context.fillStyle = shadeFor((cell.ground - survey.lowest) / band);
      // The cell's south-west corner is its minimum in both axes, so on screen
      // it is the *bottom* left: the top edge is the corner plus one cell.
      context.fillRect(toX(cell.x), toY(cell.y + CELL_TILES), side, side);
    }

    context.globalAlpha = 1;

    context.strokeStyle = SHEET.road;
    context.lineWidth = ROAD_WIDTH;
    context.lineCap = "round";
    context.beginPath();

    for (const road of survey.roads) {
      context.moveTo(toX(road.fromX), toY(road.fromY));
      context.lineTo(toX(road.toX), toY(road.toY));
    }

    context.stroke();

    if (survey.trail.length > 1) {
      context.strokeStyle = SHEET.trail;
      context.lineWidth = TRAIL_WIDTH;
      context.setLineDash(TRAIL_DASH);
      context.beginPath();

      for (const [index, point] of survey.trail.entries()) {
        if (index === 0) {
          context.moveTo(toX(point.x), toY(point.y));
        } else {
          context.lineTo(toX(point.x), toY(point.y));
        }
      }

      context.stroke();
      context.setLineDash([]);
    }

    context.font = labelFont();
    context.textBaseline = "middle";

    /*
     * Where a name has already been written, so the paper never writes two on
     * one spot.
     *
     * **A legibility rule, and it arrived with the marks.** Everything the
     * survey puts on the paper is at a *world* position, and a town's own dot, a
     * cross for the ruin it is in and the books lying in its square are all at
     * very nearly the same one — so four labels landed on top of each other and
     * the shot was of a smear. A cartographer does not solve that by leaving
     * three of them off; they stack them, and give up only when there is nowhere
     * left. So does this.
     *
     * Places are written first and marks after, which is the order of
     * importance: a town's name is the thing a reader is looking for.
     */
    const written: { x: number; y: number; width: number }[] = [];

    for (const place of survey.places) {
      const x = toX(place.x);
      const y = toY(place.y);

      context.strokeStyle = SHEET.ink;
      context.fillStyle = SHEET.ink;
      context.lineWidth = 1.2;
      context.beginPath();
      context.arc(x, y, PLACE_RADIUS, 0, Math.PI * 2);

      // Filled for a town whose streets were crossed, open for one seen from
      // somewhere else: the two ways a place gets onto the paper, drawn apart
      // (`map/survey.ts`'s `PlaceKnowledge`).
      if (place.knowledge === "visited") {
        context.fill();
      } else {
        context.stroke();
      }

      writeLabel(context, written, place.name, x + PLACE_RADIUS + 4, y);
    }

    for (const mark of survey.marks) {
      const x = toX(mark.x);
      const y = toY(mark.y);

      context.fillStyle = SHEET.mark;
      context.strokeStyle = SHEET.mark;
      context.lineWidth = 1.4;

      // The glyph, by what the mark is about (`map/ground.ts`'s `MapMarkKind`).
      // Two shapes rather than two colours: the whole page is one ink, and a
      // reader tells a book from a burned town by the shape of the scratch the
      // way they would on paper.
      if (mark.kind === "chronicle") {
        // A filled block: a book seen from above is a solid little rectangle,
        // and it is the mark a player put there themselves.
        context.fillRect(x - MARK_SIZE, y - MARK_SIZE, MARK_SIZE * 2, MARK_SIZE * 2);
      } else {
        // A cross struck through: the oldest thing on any map for a place that
        // is not there any more, and it needs no legend.
        context.beginPath();
        context.moveTo(x - MARK_SIZE, y - MARK_SIZE);
        context.lineTo(x + MARK_SIZE, y + MARK_SIZE);
        context.moveTo(x + MARK_SIZE, y - MARK_SIZE);
        context.lineTo(x - MARK_SIZE, y + MARK_SIZE);
        context.stroke();
      }

      writeLabel(context, written, mark.label, x + MARK_SIZE + 4, y);
    }

    this.draws += 1;
  }
}

/**
 * Writes one label where it does not land on one already written, or not at all.
 *
 * Tried at its own height first and then a line above and below it, twice each,
 * which is what a hand does with three things in one square. An empty label is
 * a mark that wants no name — a cross on a town whose name is already written
 * beside it, for one — and is skipped without taking a slot.
 *
 * The cost is a handful of overlap tests against a list that is never longer
 * than the number of names on the page, on a canvas redrawn when the ground
 * changes rather than every frame.
 */
function writeLabel(
  context: CanvasRenderingContext2D,
  written: { x: number; y: number; width: number }[],
  text: string,
  x: number,
  y: number,
): void {
  if (text === "") {
    return;
  }

  const width = context.measureText(text).width;

  for (const step of LABEL_STACK_STEPS) {
    const at = y + step * LABEL_LINE_HEIGHT;
    const clear = written.every(
      (already) =>
        Math.abs(already.y - at) >= LABEL_LINE_HEIGHT ||
        already.x + already.width <= x ||
        x + width <= already.x,
    );

    if (clear) {
      written.push({ x, y: at, width });
      context.fillText(text, x, at);

      return;
    }
  }
}

/**
 * The date off the end of the caller's title.
 *
 * `witness/witness.ts` composes one string — *"map — year 195 day 41"* — and
 * the page's own name is the tab now, so what is left to say is *when*. The cut
 * is at the **last** ` — `, which is the client's own connective and never a
 * sentence the simulation wrote (chunk U-2's head split, `ui.md` §3.3, and the
 * same degrade: a caller that changes its punctuation loses nothing and gets
 * its whole string drawn instead of a wrong half of one).
 *
 * *Filed*, as U-2 filed its own: the honest end-state is
 * `MapPanel.show(survey, date)` with `witness/witness.ts` handing over the date
 * alone — that file is fenced out of every chunk in this wave (invariant 10).
 */
function dateOf(title: string): string {
  const cut = title.lastIndexOf(" — ");

  return cut < 0 ? title : title.slice(cut + 3);
}

/** The line under the sheet: what the page is, and whether it has room left. */
function subtitleFor(survey: SurveyedMap): string {
  if (survey.cells.length === 0) {
    return "nothing drawn yet — walk, and the paper fills";
  }

  const full = survey.full ? " · the paper has no room left" : "";

  return `drawn from the ground you have walked${full}`;
}

/** The hypsometric tint: low ground to high, across whatever has been surveyed. */
function shadeFor(fraction: number): string {
  const at = Math.min(1, Math.max(0, fraction));

  return mix(SHEET.low, SHEET.high, at);
}

/**
 * Blends two `#rrggbb` colours.
 *
 * In sRGB rather than in a linear space, deliberately and unlike
 * `render/daylight.ts`: this is ink on paper and the two ends were picked by
 * eye in the same space, so mixing them anywhere else would move the middle
 * away from what was chosen.
 */
function mix(from: string, to: string, at: number): string {
  const left = Number.parseInt(from.slice(1), 16);
  const right = Number.parseInt(to.slice(1), 16);
  const channel = (shift: number): number =>
    Math.round(((left >> shift) & 0xff) * (1 - at) + ((right >> shift) & 0xff) * at);

  return `rgb(${channel(16)} ${channel(8)} ${channel(0)})`;
}
